/**
 * Mentorship v1 service module — wraps every /api/mentorship/* endpoint.
 *
 * Conventions follow projects.ts exactly:
 *   - Local TypeScript types declared at the top
 *   - All HTTP via apiClient (axios instance with auth + token refresh)
 *   - Functions throw on error (no union return types)
 *   - Paginated responses unwrap to { results, count, next, previous }
 *
 * SSOT: the JSON shapes here MUST match the structured-decision-engine
 * spec verbatim where the spec specifies a shape (Assessment,
 * AssessmentReview, FeedbackTemplate, Report, Insight, ClientProfile, Risk,
 * EngagementSummary, LearningUpdate). MentorshipProject is not in the spec
 * but is required by the workflow — it lives in the same namespace.
 */

import { apiClient } from './apiClient';
import { apiUrl } from './apiUrls';
import { captureError } from './core/errors/captureError';
import {
    AssessmentReviewSchema,
    AssessmentSchema,
    ClientProfileSchema,
    ConcernHistoryEntrySchema,
    FeedbackTemplateSchema,
    InsightSchema,
    MeetingSchema,
    MentorshipProjectSchema,
    PaginatedSchema,
    PairingStatusSchema,
    ReportDisputeSchema,
    ReportSchema,
    RiskSchema,
    SubmissionSchema,
    type FinalizedReport,
    type PairingStatusWire,
} from './schemas/mentorship';
import { ResponseShapeError } from './schemas/types';
import { z } from 'zod';
import type { ZodTypeAny, ZodError } from 'zod';

/**
 * Boundary safeParse helper — Phase 0 of ADR-0002.
 *
 * Every READ endpoint wrapped in this slice calls `parseAtBoundary` so
 * we have ONE site that:
 *   1. Calls `safeParse` (never `.parse`) — no raw `ZodError` escapes.
 *   2. Forwards the failure to `captureError` (round-18 SSOT) with the
 *      caller's `scope` + `op` + the Zod issue list as `extra.issues`.
 *      Sentry / analytics gets a structured payload per failure.
 *   3. Throws a typed `ResponseShapeError` so the UI layer can branch
 *      (RetryBanner shows a friendlier "server returned unexpected
 *      data; the team has been notified" copy on this class).
 *
 * Keeping this helper LOCAL to the service module — not exported —
 * stops other call-sites from re-inventing the captureError/throw
 * shape. When the ts-morph sweep (ADR-0002 §4) lands it will rewrite
 * the remaining ~25 endpoints to call this same helper.
 *
 * The structural return type is the inferred Zod output. Callers cast
 * to their hand-rolled TS type because the existing types in this file
 * are the structural SSOT for component code; the schemas are the
 * runtime guard at the boundary, deliberately TOLERANT (forward-compat
 * for optional fields) so a serializer growing a new column does not
 * crash a parse mid-flight.
 */
function parseAtBoundary<S extends ZodTypeAny>(
    schema: S,
    raw: unknown,
    ctx: { op: string },
): ReturnType<S['parse']> {
    const result = schema.safeParse(raw);
    if (!result.success) {
        const zodError: ZodError = result.error;
        // SSOT telemetry — captureError swallows sink failure internally
        // so the throw below is the only thing the caller observes. The
        // `extra.issues` payload IS the raw Zod issue list; Sentry sees
        // it as a structured object on the error event for grouping.
        captureError(zodError, {
            scope: 'mentorshipService',
            op: ctx.op,
            extra: { issues: zodError.issues },
        });
        throw new ResponseShapeError(ctx.op, zodError);
    }
    return result.data as ReturnType<S['parse']>;
}

// ─── Observability contract version ──────────────────────────────────────
//
// Pins the FE side of the §22.39 cycle-observability contract. The BE
// side exports the same constant from `education/services.py`; CI
// parity (BE constant === FE constant) is a P2 follow-up — for now
// any drift is a manual review concern at PR time. Bumping rules per
// §22.39 versioning policy: MINOR bump on additive events / optional
// fields; MAJOR bump on rename / required removal / replay-semantics
// change. Producers and consumers must read this constant at runtime,
// never inline the literal.
export const CONTRACT_VERSION = '1.0.0';

// ─── Shared enum types ──────────────────────────────────────────────────

export type ModuleKey =
    | 'concept'
    | 'design'
    | 'authorities'
    | 'contract'
    | 'tender'
    | 'construction';

/** Display labels for the six framework modules. SSOT — every component
 * that needs to render a module name in human-readable form imports this
 * constant instead of declaring its own copy. Keep in sync with the backend
 * MODULE_LABELS in education/services.py. */
export const MODULE_LABELS: Record<ModuleKey, string> = {
    concept: 'Concept / Due Diligence',
    design: 'Design',
    authorities: 'Authorities',
    contract: 'Contract',
    tender: 'Tender',
    construction: 'Construction',
};

/** Stable ordering for module-keyed iteration (matches the framework
 * progression Concept → Construction). */
export const MODULE_ORDER: ModuleKey[] = [
    'concept',
    'design',
    'authorities',
    'contract',
    'tender',
    'construction',
];

export type LevelKey = 'Low' | 'Mid' | 'Senior';

export type ConfidenceTier = 'low' | 'medium' | 'high';

export type Priority = 'low' | 'medium' | 'high';

export type SubmissionState = 'submitted' | 'assessed' | 'reviewed' | 'finalized';

export type AssessmentState = 'pending_ai' | 'ready' | 'schema_error';

export type RoleKey = 'mentor' | 'mentee' | 'admin' | null;

export type RoleRequestType = 'become_mentee' | 'become_mentor' | 'change_mentor';

export type RoleRequestState = 'pending' | 'approved' | 'rejected' | 'superseded';

export type InsightType = 'behavioral' | 'technical' | 'risk';

export type InsightSource = 'assessment' | 'mentor' | 'system';

export type ClientProfileVisibility = 'org_wide' | 'mentor_curated';

// ─── Entity shapes ──────────────────────────────────────────────────────

export type UserMini = {
    id: number;
    first_name: string;
    last_name: string;
    email: string;
};

export type Submission = {
    id: string;
    mentee: number;
    mentee_summary: UserMini | null;
    assigned_mentor: number | null;
    module: ModuleKey;
    quarter: string;
    content: string;
    state: SubmissionState;
    is_locked: boolean;
    client_submission_id: string;
    assessment_id: string | null;
    submitted_at: string;
    updated_at: string;
};

export type AssessmentJustification = {
    strengths: string[];
    gaps: string[];
    incorrect_assumptions: string[];
};

export type AssessmentSignals = {
    keywords: string[];
    missing_concepts: string[];
    risk_awareness: string[];
    thinking_patterns: {
        proactive: boolean;
        if_then: boolean;
    };
};

export type Assessment = {
    id: string;
    submission: string;
    module: ModuleKey;
    state: AssessmentState;
    score_ai: number | null;
    confidence: number | null;
    justification: AssessmentJustification;
    signals: AssessmentSignals;
    flags: string[];
    mentor_prompts: string[];
    model_name: string;
    prompt_version: string;
    rubric_version: string;
    error_detail: string;
    latency_ms: number | null;
    token_usage: Record<string, number>;
    created_at: string;
    updated_at: string;
};

export type AssessmentReview = {
    id: string;
    assessment: string;
    suggested_score_range: [number, number];
    level_mapping: LevelKey;
    review_focus: string[];
    confidence: number;
    model_name: string;
    prompt_version: string;
    created_at: string;
    updated_at: string;
};

export type FeedbackTemplate = {
    id: string;
    assessment: string;
    quick_options: string[];
    focus_areas: string[];
    editable: boolean;
    prompt_version: string;
    created_at: string;
    updated_at: string;
};

/** Minimal Submission projection inlined on Report so the mentee's
 * ReportViewer can render "Module, Quarter" context without a second
 * round-trip. Mirrors ``ReportSubmissionSummarySerializer`` on the backend. */
export type ReportSubmissionSummary = {
    id: string;
    module: ModuleKey;
    quarter: string;
    submitted_at: string;
};

export type Report = {
    id: string;
    assessment: string;
    submission: string;
    submission_summary: ReportSubmissionSummary;
    mentee: number;
    version: number;
    final_score: number;
    agreed_level: LevelKey;
    strengths: string[];
    gaps: string[];
    actions: string[];
    mentor_notes: string;
    override_reason: string;
    benchmark_tag: string;
    supersedes: string | null;
    finalized_by: number;
    timestamp: string;
};

export type Insight = {
    id: string;
    user: number | null;
    type: InsightType;
    priority: Priority;
    trigger_context: string;
    prompt: string;
    source: InsightSource;
    source_report: string | null;
    is_active: boolean;
    accepted_count: number;
    dismissed_count: number;
    created_at: string;
    updated_at: string;
};

export type ClientProfile = {
    id: string;
    client_id: string;
    priorities: string[];
    behaviors: string[];
    management_advice: string[];
    visibility: ClientProfileVisibility;
    created_by: number;
    created_at: string;
    updated_at: string;
};

export type MentorshipProject = {
    id: string;
    name: string;
    client_id: string;
    description: string;
    mentee_id: number;
    mentor_id: number | null;
    start_date: string | null;
    end_date: string | null;
    is_active: boolean;
    created_by_id: number;
    created_at: string;
    updated_at: string;
    risks_count: number;
};

export type Risk = {
    id: string;
    project: string;
    trigger: string;
    impact: string;
    mitigation: string;
    priority: Priority;
    is_active: boolean;
    created_by: number;
    created_at: string;
    updated_at: string;
};

export type EngagementSummary = {
    entity: 'EngagementSummary';
    user_id: number;
    period: string;
    top_topics: string[];
    issues: string[];
    improvements: string[];
    effective_prompts: string[];
    ignored_prompts: string[];
    mentor_focus: string;
};

export type EngagementSummarySnapshot = {
    id: string;
    user: number;
    period_start: string;
    period_end: string;
    top_topics: string[];
    issues: string[];
    improvements: string[];
    effective_prompts: string[];
    ignored_prompts: string[];
    mentor_focus: string;
    generated_at: string;
    generated_by: number | null;
};

export type LearningUpdatePattern = {
    pattern: string;
    evidence_count: number;
    sample_user_ids: number[];
};

export type LearningUpdate = {
    id: string;
    patterns_detected: LearningUpdatePattern[];
    rules_updated: string[];
    system_recommendations: string[];
    period_start: string;
    period_end: string;
    generated_at: string;
    generated_by: string;
    applied_at: string | null;
};

export type RoleRequest = {
    id: string;
    user: number;
    request_type: RoleRequestType;
    state: RoleRequestState;
    payload: Record<string, unknown>;
    decided_by: number | null;
    decision_reason: string;
    created_at: string;
    updated_at: string;
};

export type RoleAuditLogRow = {
    id: string;
    actor: number;
    target: number;
    field: string;
    old_value: unknown;
    new_value: unknown;
    reason: string;
    created_at: string;
};

export type Notification = {
    id: string;
    type: string;
    payload: Record<string, unknown>;
    read_at: string | null;
    created_at: string;
};

export type UserSummary = {
    id: number;
    username: string;
    email: string;
    first_name: string;
    last_name: string;
    role: RoleKey;
    mentor_id: number | null;
    mentor_specialties: ModuleKey[];
    mentor_capacity: number | null;
    active_load: number;
};

// ─── Pagination envelope ────────────────────────────────────────────────

export type PagedResponse<T> = {
    count: number;
    next: string | null;
    previous: string | null;
    results: T[];
};

function url(path: string): string {
    return apiUrl(path);
}

// ────────────────────────────────────────────────────────────────────────
// Submissions
// ────────────────────────────────────────────────────────────────────────

export async function getSubmissions(
    filters: { state?: SubmissionState; module?: ModuleKey; page?: number } = {},
): Promise<PagedResponse<Submission>> {
    const params = new URLSearchParams();
    if (filters.state) params.set('state', filters.state);
    if (filters.module) params.set('module', filters.module);
    if (filters.page) params.set('page', String(filters.page));
    const qs = params.toString();
    const response = await apiClient.get(url(`/api/mentorship/submissions/${qs ? '?' + qs : ''}`));
    return response.data;
}

// ────────────────────────────────────────────────────────────────────────
// Cursor-paginated sibling functions (Tier 3 — backend commit 028097f7)
// ────────────────────────────────────────────────────────────────────────
//
// Each `*Page` function below is the cursor-pagination twin of an
// existing `get*` / `list*` reader. Why a sibling rather than mutating
// the existing signature?
//
//   - Tier 1 / Tier 2 consumers (SubmissionDetailModal,
//     MentorReviewDashboard's single-fetch path, etc.) still expect the
//     legacy `{ count, next, previous, results }` envelope. Mutating
//     would force a cross-cutting migration in this slice.
//   - The 7th endpoint (listDisputes) currently returns a bare array;
//     reshaping that return is a breaking change. A sibling is
//     strictly additive.
//   - Rollback ladder: if the backend cursor envelope misbehaves in
//     canary, flip BackendCapabilities.hasCursorPagination=false and
//     delete the import sites in NeedsAttentionView — legacy readers
//     are untouched.
//
// Wire-shape envelope (post-028097f7):
//   { results: T[], next_cursor: string | null, count?: number }
//
// FE return shape (camelCase):
//   { items: T[]; nextCursor: string | null; totalCount: number | null }
//
// Each function:
//   1. Builds the query string from filters.
//   2. Spreads in `cursor` only when truthy (no `?cursor=null`).
//   3. Zod-parses the envelope at the boundary (PaginatedSchema(...)).
//   4. Re-casts envelope.results to the structural TS type. The Zod
//      schemas are deliberately tolerant supersets (forward-compat);
//      the structural cast picks the canonical FE type for callers.

/** Page shape returned by every cursor-paginated reader. */
export type CursorPage<T> = {
    items: T[];
    nextCursor: string | null;
    totalCount: number | null;
};

export async function getSubmissionsPage(
    opts: {
        state?: SubmissionState;
        module?: ModuleKey;
        cursor?: string | null;
    } = {},
): Promise<CursorPage<Submission>> {
    const params = new URLSearchParams();
    if (opts.state) params.set('state', opts.state);
    if (opts.module) params.set('module', opts.module);
    if (opts.cursor) params.set('cursor', opts.cursor);
    const qs = params.toString();
    const response = await apiClient.get(
        url(`/api/mentorship/submissions/${qs ? '?' + qs : ''}`),
    );
    // ADR-0002 Phase 1 Tier A: safeParse boundary helper — converts
    // the legacy `.parse()` site to the typed-error contract so a
    // serializer drift surfaces as `ResponseShapeError` instead of a
    // raw ZodError exploding three components downstream.
    const envelope = parseAtBoundary(
        PaginatedSchema(SubmissionSchema),
        response.data,
        { op: 'getSubmissionsPage' },
    );
    return {
        items: envelope.results as unknown as Submission[],
        nextCursor: envelope.next_cursor,
        totalCount: envelope.count ?? null,
    };
}

export async function listReportsPage(
    opts: {
        mentee_id?: number;
        module?: ModuleKey;
        cursor?: string | null;
    } = {},
): Promise<CursorPage<Report>> {
    const params = new URLSearchParams();
    if (opts.mentee_id) params.set('mentee_id', String(opts.mentee_id));
    if (opts.module) params.set('module', opts.module);
    if (opts.cursor) params.set('cursor', opts.cursor);
    const qs = params.toString();
    const response = await apiClient.get(
        url(`/api/mentorship/reports/${qs ? '?' + qs : ''}`),
    );
    // ADR-0002 Phase 1 Tier A: safeParse boundary helper.
    const envelope = parseAtBoundary(
        PaginatedSchema(ReportSchema),
        response.data,
        { op: 'listReportsPage' },
    );
    return {
        items: envelope.results as unknown as Report[],
        nextCursor: envelope.next_cursor,
        totalCount: envelope.count ?? null,
    };
}

export async function listInsightsPage(
    opts: {
        user_id?: number;
        type?: InsightType;
        priority?: Priority;
        cursor?: string | null;
    } = {},
): Promise<CursorPage<Insight>> {
    const params = new URLSearchParams();
    if (opts.user_id) params.set('user_id', String(opts.user_id));
    if (opts.type) params.set('type', opts.type);
    if (opts.priority) params.set('priority', opts.priority);
    if (opts.cursor) params.set('cursor', opts.cursor);
    const qs = params.toString();
    const response = await apiClient.get(
        url(`/api/mentorship/insights/${qs ? '?' + qs : ''}`),
    );
    // ADR-0002 Phase 1 Tier A: safeParse boundary helper.
    const envelope = parseAtBoundary(
        PaginatedSchema(InsightSchema),
        response.data,
        { op: 'listInsightsPage' },
    );
    return {
        items: envelope.results as unknown as Insight[],
        nextCursor: envelope.next_cursor,
        totalCount: envelope.count ?? null,
    };
}

export async function listMentorshipProjectsPage(
    opts: {
        mentee_id?: number;
        mentor_id?: number;
        client_id?: string;
        active?: boolean;
        cursor?: string | null;
    } = {},
): Promise<CursorPage<MentorshipProject>> {
    const params = new URLSearchParams();
    if (opts.mentee_id) params.set('mentee_id', String(opts.mentee_id));
    if (opts.mentor_id) params.set('mentor_id', String(opts.mentor_id));
    if (opts.client_id) params.set('client_id', opts.client_id);
    if (opts.active === false) params.set('active', '0');
    if (opts.cursor) params.set('cursor', opts.cursor);
    const qs = params.toString();
    const response = await apiClient.get(
        url(`/api/mentorship/projects/${qs ? '?' + qs : ''}`),
    );
    // ADR-0002 Phase 1 Tier A: safeParse boundary helper.
    const envelope = parseAtBoundary(
        PaginatedSchema(MentorshipProjectSchema),
        response.data,
        { op: 'listMentorshipProjectsPage' },
    );
    return {
        items: envelope.results as unknown as MentorshipProject[],
        nextCursor: envelope.next_cursor,
        totalCount: envelope.count ?? null,
    };
}

export async function listClientProfilesPage(
    opts: { client_id?: string; cursor?: string | null } = {},
): Promise<CursorPage<ClientProfile>> {
    const params = new URLSearchParams();
    if (opts.client_id) params.set('client_id', opts.client_id);
    if (opts.cursor) params.set('cursor', opts.cursor);
    const qs = params.toString();
    const response = await apiClient.get(
        url(`/api/mentorship/client_profiles/${qs ? '?' + qs : ''}`),
    );
    // ADR-0002 Phase 1 Tier A: safeParse boundary helper.
    const envelope = parseAtBoundary(
        PaginatedSchema(ClientProfileSchema),
        response.data,
        { op: 'listClientProfilesPage' },
    );
    return {
        items: envelope.results as unknown as ClientProfile[],
        nextCursor: envelope.next_cursor,
        totalCount: envelope.count ?? null,
    };
}

export async function listDisputesPage(
    opts: {
        state?: 'open' | 'resolved' | 'withdrawn';
        cursor?: string | null;
        signal?: AbortSignal;
    } = {},
): Promise<CursorPage<ReportDispute>> {
    const params = new URLSearchParams();
    if (opts.state) params.set('state', opts.state);
    if (opts.cursor) params.set('cursor', opts.cursor);
    const qs = params.toString();
    const response = await apiClient.get(
        url(`/api/mentorship/disputes/${qs ? '?' + qs : ''}`),
        opts.signal ? { signal: opts.signal } : undefined,
    );
    // ADR-0002 Phase 0: safeParse boundary helper — converts the legacy
    // `.parse()` site to the typed-error contract so a serializer drift
    // surfaces as `ResponseShapeError` (caught by RetryBanner's
    // shape-specific branch) instead of a raw ZodError. This wraps both
    // `listDisputesPage` AND `listDisputes` (the latter delegates here).
    const envelope = parseAtBoundary(
        PaginatedSchema(ReportDisputeSchema),
        response.data,
        { op: 'listDisputes' },
    );
    return {
        items: envelope.results as unknown as ReportDispute[],
        nextCursor: envelope.next_cursor,
        totalCount: envelope.count ?? null,
    };
}

export async function createSubmission(
    module: ModuleKey,
    content: string,
    clientSubmissionId: string,
    clientRequestId?: string,
): Promise<Submission> {
    // Dedup key defaults to clientSubmissionId — it's already the FE's
    // identity for the row, so a naïve retry replays the same key and
    // the backend collapses (org_id, key, endpoint) into one row instead
    // of two.
    const response = await apiClient.post(
        url('/api/mentorship/submissions/'),
        { module, content, client_submission_id: clientSubmissionId },
        // ADR-0001 §2.1: canonical `X-Idempotency-Key` (prefixed). The
        // unprefixed legacy form is rejected at backend ingress.
        { headers: { 'X-Idempotency-Key': clientRequestId ?? clientSubmissionId } },
    );
    // ADR-0002 Phase 1 mutation sweep: WRITE response wrapped via the
    // safeParse boundary helper.
    return parseAtBoundary(SubmissionSchema, response.data, {
        op: 'createSubmission',
    }) as unknown as Submission;
}

export async function getSubmission(submissionId: string): Promise<Submission> {
    const response = await apiClient.get(url(`/api/mentorship/submissions/${submissionId}/`));
    // ADR-0002 Phase 1 Tier A: safeParse boundary helper — backend
    // serializer drift surfaces as a typed `ResponseShapeError` (with
    // structured Zod issue list on telemetry) instead of an
    // undefined-property crash three components downstream.
    return parseAtBoundary(SubmissionSchema, response.data, {
        op: 'getSubmission',
    }) as unknown as Submission;
}

export async function retryAssessment(submissionId: string): Promise<Assessment> {
    const response = await apiClient.post(
        url(`/api/mentorship/submissions/${submissionId}/retry_assessment/`),
    );
    return response.data;
}

// ────────────────────────────────────────────────────────────────────────
// Mentee hint LLM affordance (WS-FTUE-5)
// ────────────────────────────────────────────────────────────────────────
//
// `requestMenteeHint` POSTs to /api/mentorship/submissions/{id}/hint/ and
// returns a short LLM-generated nudge. The client-side hook
// (useMenteeHint) is the SSOT for rate-limiting (3 calls / submission /
// session). The server is also expected to enforce its own limit + the
// Socratic-eval + PII-echo gates; this slice is FE-only and trusts the
// backend to bounce abusive callers with 429.
//
// Zod runtime parsing is deferred to the parallel schema slice — for now
// the shape is enforced at the TS boundary only. The hint text is the
// only field consumers render today; ``remaining`` is a courtesy field
// the backend may surface so the FE can mirror the rate-limit counter.

export type HintResponse = {
    /** Short LLM-generated nudge. ≤ 200 chars by contract. */
    hint: string;
    /** Optional remaining-calls hint from the server. Absent when the
     *  backend does not expose its own counter. */
    remaining?: number;
};

export async function requestMenteeHint(
    submissionId: string,
): Promise<HintResponse> {
    const response = await apiClient.post(
        url(`/api/mentorship/submissions/${submissionId}/hint/`),
    );
    return response.data as HintResponse;
}

export async function assignSubmissionMentor(
    submissionId: string,
    mentorId: number,
    reason: string,
): Promise<Submission> {
    const response = await apiClient.patch(
        url(`/api/mentorship/submissions/${submissionId}/assign/`),
        { mentor_id: mentorId, reason },
    );
    return response.data;
}

// ────────────────────────────────────────────────────────────────────────
// Assessment / AssessmentReview / FeedbackTemplate
// ────────────────────────────────────────────────────────────────────────

export async function getAssessment(assessmentId: string): Promise<Assessment> {
    const response = await apiClient.get(url(`/api/mentorship/assessments/${assessmentId}/`));
    // ADR-0002 Phase 1 Tier B: boundary parse with newly-authored
    // AssessmentSchema. Detail render in many places — drift surfaces
    // as a typed `ResponseShapeError` (RetryBanner shape branch).
    return parseAtBoundary(AssessmentSchema, response.data, {
        op: 'getAssessment',
    }) as unknown as Assessment;
}

export async function getAssessmentReview(assessmentId: string): Promise<AssessmentReview> {
    const response = await apiClient.get(
        url(`/api/mentorship/assessment_reviews/${assessmentId}/`),
    );
    // ADR-0002 Phase 1 Tier B: boundary parse with AssessmentReviewSchema.
    return parseAtBoundary(AssessmentReviewSchema, response.data, {
        op: 'getAssessmentReview',
    }) as unknown as AssessmentReview;
}

export async function regenerateAssessmentReview(
    assessmentId: string,
): Promise<AssessmentReview> {
    const response = await apiClient.post(
        url(`/api/mentorship/assessment_reviews/${assessmentId}/regenerate/`),
    );
    return response.data;
}

export async function getFeedbackTemplate(assessmentId: string): Promise<FeedbackTemplate> {
    const response = await apiClient.get(
        url(`/api/mentorship/feedback_templates/${assessmentId}/`),
    );
    // ADR-0002 Phase 1 Tier B: boundary parse with FeedbackTemplateSchema.
    return parseAtBoundary(FeedbackTemplateSchema, response.data, {
        op: 'getFeedbackTemplate',
    }) as unknown as FeedbackTemplate;
}

export async function regenerateFeedbackTemplate(
    assessmentId: string,
): Promise<FeedbackTemplate> {
    const response = await apiClient.post(
        url(`/api/mentorship/feedback_templates/${assessmentId}/regenerate/`),
    );
    return response.data;
}

// ────────────────────────────────────────────────────────────────────────
// Reports
// ────────────────────────────────────────────────────────────────────────

export type CreateReportInput = {
    assessment_id: string;
    final_score: number;
    agreed_level: LevelKey;
    strengths: string[];
    gaps: string[];
    actions: string[];
    mentor_notes?: string | undefined;
    override_reason?: string | undefined;
    benchmark_tag?: string | undefined;
};

export async function createReport(
    input: CreateReportInput,
    clientRequestId?: string,
): Promise<Report> {
    const response = await apiClient.post(
        url('/api/mentorship/reports/'),
        input,
        clientRequestId
            // ADR-0001 §2.1: canonical `X-Idempotency-Key` (prefixed).
            ? { headers: { 'X-Idempotency-Key': clientRequestId } }
            : undefined,
    );
    // ADR-0002 Phase 1 mutation sweep: WRITE response wrapped via the
    // safeParse boundary helper. Same fail-fast + ResponseShapeError +
    // captureError telemetry path as the READ wraps.
    return parseAtBoundary(ReportSchema, response.data, {
        op: 'createReport',
    }) as unknown as Report;
}

/**
 * Tier 2 sub-slice 3 — mentor finalize endpoint
 * (POST /api/education/mentorship/submissions/{id}/reports/).
 *
 * This is the modern, idempotent, ETag-conditional write path that
 * replaces the legacy `/api/mentorship/reports/` POST. Surface the
 * canonical contract here so component code only has to pick the
 * right inputs.
 *
 * Headers:
 *   - `If-Match`: precondition for the conditional write. The
 *     canonical source under WS-M-1 is the explicit `ifMatch`
 *     parameter — it carries `String(report.version)` so the backend
 *     `check_etag_or_409` decorator (BE commit 81d7b84f) can compare
 *     the caller's expected version against the server-side row and
 *     return 409 `precondition_failed` instead of last-writer-wins.
 *     For backwards-compatibility with the legacy submission-ETag
 *     precondition path, `submissionETag` is still honoured when
 *     `ifMatch` is absent — the new contract WINS when both are
 *     supplied (callers wiring WS-M-1 always pass `ifMatch`). The
 *     spread-conditional pattern is REQUIRED under
 *     `exactOptionalPropertyTypes: true` (no `If-Match: undefined`).
 *   - `X-Idempotency-Key`: defaults to `clientReviewId`. Backend dedupes
 *     on (organisation_id, key, endpoint), so callers get safe replay
 *     for free — sending the same request twice produces the same
 *     report (200) instead of two rows (201 + 201). ADR-0001 §2.1
 *     pins the `X-` prefix as the sole canonical FE header name.
 *
 * Response:
 *   - 201 → newly created report (created=true).
 *   - 200 → idempotent replay; the server returned the existing row.
 *   - `submissionETag` is the response `ETag` header (server-supplied,
 *     used for the next conditional write).
 *
 * Errors propagate as raw AxiosError so the caller can branch on
 * `err.response?.status` (412 = legacy submission ETag mismatch, 409 =
 * WS-M-1 `precondition_failed` from the report-version check or
 * `state_invalid`, 410 = endpoint_retired). The shared `reportError()`
 * helper classifies those; the component layer overrides messaging
 * where it has context.
 */
export type FinalizeMentorReviewInput = {
    submissionId: string;
    assessmentId: string;
    mentorFeedback: string;
    mentorScore: number;
    clientReviewId: string;
    /** Strong submission ETag, e.g. `"sha256-<32hex>"`. Absent → no
     *  conditional precondition (acceptable on first write).
     *
     *  LEGACY: this is the pre-WS-M-1 submission-ETag precondition.
     *  Callers wiring the WS-M-1 report-version contract should pass
     *  `ifMatch` instead — when both are supplied, `ifMatch` wins. */
    submissionETag?: string | null | undefined;
    /**
     * WS-M-1 explicit `If-Match` value — stringified `report.version`
     * that the caller stashed when the report was last fetched. The
     * backend `check_etag_or_409` decorator (BE commit 81d7b84f) uses
     * this to detect concurrent edits and return 409
     * `precondition_failed` rather than last-writer-wins.
     *
     * When supplied this WINS over `submissionETag` (same header name,
     * report-version semantics). Omit it on the Force-finalize path so
     * the BE accepts the overwrite and logs the override.
     */
    ifMatch?: string | undefined;
    /** Override the idempotency key. Defaults to `clientReviewId` so
     *  the FE never accidentally double-creates on retry. */
    idempotencyKey?: string | undefined;
};

export type FinalizeMentorReviewResult = {
    report: FinalizedReport;
    submissionETag: string | null;
    created: boolean;
};

export async function finalizeMentorReview(
    input: FinalizeMentorReviewInput,
): Promise<FinalizeMentorReviewResult> {
    const {
        submissionId,
        assessmentId,
        mentorFeedback,
        mentorScore,
        clientReviewId,
        submissionETag,
        ifMatch,
        idempotencyKey,
    } = input;

    // WS-M-1 canonical: `X-Idempotency-Key` (with `X-` prefix). The ADR
    // §2.1 pins this as the sole FE-emitted header name so backend
    // middleware can reject the unprefixed `Idempotency-Key` at ingress
    // and prevent dual-write tolerance. The prefix matches the
    // `mentorship/0029_*` dedupe-table header lookup expectation.
    //
    // WS-M-1 `If-Match`: prefer the explicit `ifMatch` (stringified
    // `report.version`, paired with the BE `check_etag_or_409`
    // decorator on `submission_reports_view`). Fall back to the legacy
    // `submissionETag` when `ifMatch` is absent so non-WS-M-1 callers
    // keep working. Omitted entirely when neither is supplied — the
    // Force-finalize path passes `ifMatch: undefined` deliberately so
    // the BE accepts the override and audits the bypass.
    const ifMatchHeaderValue = ifMatch ?? submissionETag ?? null;
    const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        'X-Idempotency-Key': idempotencyKey ?? clientReviewId,
        ...(ifMatchHeaderValue ? { 'If-Match': ifMatchHeaderValue } : {}),
    };

    const response = await apiClient.post(
        url(`/api/education/mentorship/submissions/${submissionId}/reports/`),
        {
            client_review_id: clientReviewId,
            assessment_id: assessmentId,
            mentor_feedback: mentorFeedback,
            mentor_score: mentorScore,
        },
        { headers },
    );

    // ADR-0002 Phase 1 mutation sweep: migrate the legacy bare
    // `ReportSchema.parse(...)` to the safeParse boundary helper so a
    // serializer drift on the critical finalize WRITE surfaces as a
    // typed `ResponseShapeError` (with structured Zod issue list on
    // telemetry) instead of a raw ZodError exploding three components
    // downstream. Same fail-fast posture as the Tier B read wraps.
    const report = parseAtBoundary(ReportSchema, response.data, {
        op: 'finalizeMentorReview',
    });
    const etagHeader = response.headers?.etag ?? response.headers?.ETag ?? null;

    return {
        report,
        submissionETag: typeof etagHeader === 'string' ? etagHeader : null,
        created: response.status === 201,
    };
}

export type ReportDispute = {
    id: string;
    report: string;
    mentee: number;
    mentee_summary: UserMini | null;
    reason: string;
    // TODO(§11.5 row 1) — backend ReportDispute state machine currently
    // has 3 values (open/resolved/withdrawn). Plan §8.1 / WS-M-3 adds
    // `in_review` + `escalated` behind the concern-state-machine flag.
    // Widen this union (and BackendCapabilities.hasFiveDisputeStates →
    // true in src/types/backendCapabilities.ts) when the flag flips.
    state: 'open' | 'resolved' | 'withdrawn';
    resolved_by: number | null;
    resolution_note: string;
    created_at: string;
    resolved_at: string | null;
};

export async function requestReportReview(
    reportId: string, reason: string,
): Promise<ReportDispute> {
    const response = await apiClient.post(
        url(`/api/mentorship/reports/${reportId}/dispute/`),
        { reason },
    );
    return response.data;
}

export async function listReportDisputes(reportId: string): Promise<ReportDispute[]> {
    const response = await apiClient.get(
        url(`/api/mentorship/reports/${reportId}/dispute/`),
    );
    // ADR-0002 Phase 1 Tier B: boundary parse on bare-array response.
    return parseAtBoundary(ReportDisputeSchema.array(), response.data, {
        op: 'listReportDisputes',
    }) as unknown as ReportDispute[];
}

/** List disputes visible to the viewer. Mentors see disputes on the
 *  reports they finalised; admins see all; mentees see their own.
 *
 *  Returns ALL visible items on the first page. Callers that need to
 *  paginate further use `listDisputesPage` directly with a cursor.
 *
 *  Wire shape: `/api/mentorship/disputes/` is HmacCursorPagination
 *  (post-WS-ENT-2). This function delegates to `listDisputesPage` so
 *  there is ONE place in the codebase that parses the disputes
 *  envelope. No bare-array fallback. */
export async function listDisputes(
    state?: 'open' | 'resolved' | 'withdrawn',
    opts: { signal?: AbortSignal } = {},
): Promise<ReportDispute[]> {
    const page = await listDisputesPage(state ? { state, ...opts } : opts);
    return page.items;
}

/** Mentor/admin closes a dispute. Optional ``note`` is stored on the
 *  row + included in the notification payload to the mentee. */
export async function resolveReportDispute(
    disputeId: string, note: string = '',
): Promise<ReportDispute> {
    const response = await apiClient.post(
        url(`/api/mentorship/disputes/${disputeId}/resolve/`),
        { note },
    );
    // ADR-0002 Phase 1 mutation sweep: WRITE response wrapped via the
    // safeParse boundary helper.
    return parseAtBoundary(ReportDisputeSchema, response.data, {
        op: 'resolveReportDispute',
    }) as unknown as ReportDispute;
}

/** Mentee rescinds their own dispute. Terminal — a new dispute row is
 *  needed if they change their mind again. */
export async function withdrawReportDispute(
    disputeId: string, note: string = '',
): Promise<ReportDispute> {
    const response = await apiClient.post(
        url(`/api/mentorship/disputes/${disputeId}/withdraw/`),
        { note },
    );
    // ADR-0002 Phase 1 mutation sweep: WRITE response wrapped via the
    // safeParse boundary helper.
    return parseAtBoundary(ReportDisputeSchema, response.data, {
        op: 'withdrawReportDispute',
    }) as unknown as ReportDispute;
}

// ────────────────────────────────────────────────────────────────────────
// WS-M-3 Concern state machine — bidirectional Concern (Atlassian-style)
//
// New endpoints, gated behind the `concern-state-machine` flag while the
// backend `disputes.state + disputes.version` migration is in flight. The
// returned shapes alias `ReportDispute` (the wire payload is unchanged —
// the migration only WIDENS the state union to include `in_review` and
// `escalated`).
//
// All POST/PATCH calls attach the canonical `X-Idempotency-Key` header
// (ADR-0001 §2.1). The unprefixed legacy `Idempotency-Key` is banned —
// adding it here would silently bypass the dedupe table when the
// backend middleware rejects the prefixed form.
// ────────────────────────────────────────────────────────────────────────

/** Action verbs accepted by the backend transition endpoint. Mirrors
 *  the FE state-machine `ConcernAction` union; duplicated here so the
 *  service module stays decoupled from the concern/ module. */
export type ConcernTransitionAction =
    | 'advance'
    | 'resolve'
    | 'withdraw'
    | 'escalate';

/** One row of the bidirectional Concern timeline. The backend emits
 *  these from the `disputes_state_history` table that lands with the
 *  WS-M-3 backend migration. Until the migration ships, the
 *  `concern-state-machine` flag gates the caller — the FE module
 *  contract stays stable across the cutover. */
export type ConcernHistoryEntry = {
    id: string;
    /** Parent `disputes.id` (the wire field stays "dispute" — the
     *  data-model name leaks into the FK column. The UI labels every
     *  surface as "Concern".) */
    dispute: string;
    state: 'open' | 'in_review' | 'resolved' | 'withdrawn' | 'escalated';
    actor: 'mentor' | 'mentee' | 'admin';
    actor_id: number | null;
    actor_label: string;
    comment: string;
    timestamp: string;
};

/**
 * Mentee raises a new Concern against a finalised Report.
 *
 * POST `/api/mentorship/reports/{reportId}/concerns/`. The backend
 * creates a row with state='open' and emits the first
 * ConcernHistoryEntry. Idempotent — replaying the same
 * (reportId, clientRequestId) returns the existing row.
 *
 * @param reportId         Target report UUID.
 * @param reason           Mentee's free-text reason (min 20 chars; the
 *                         modal validates this client-side too).
 * @param clientRequestId  Optional dedupe key. Defaults are the caller's
 *                         responsibility — typically a `crypto.randomUUID()`
 *                         generated when the modal opens.
 */
export async function createConcern(
    reportId: string,
    reason: string,
    clientRequestId?: string,
): Promise<ReportDispute> {
    const response = await apiClient.post(
        url(`/api/mentorship/reports/${reportId}/concerns/`),
        { reason },
        clientRequestId
            ? { headers: { 'X-Idempotency-Key': clientRequestId } }
            : undefined,
    );
    // ADR-0002 Phase 1 mutation sweep: WRITE response wrapped via the
    // safeParse boundary helper. The wire payload is structurally a
    // `ReportDispute` (the Concern surface is the same row — see
    // ConcernHistoryEntry comments above).
    return parseAtBoundary(ReportDisputeSchema, response.data, {
        op: 'createConcern',
    }) as unknown as ReportDispute;
}

/**
 * Advance a Concern through the state machine. Wraps the backend
 * `PATCH /api/mentorship/concerns/{concernId}/transition/` endpoint
 * which validates the (current_state, action, actor) triple server-side
 * against the same matrix encoded in `concernStateMachine.ts`.
 *
 * Caller MUST validate the transition locally via
 * `concernStateMachine.transition(...)` BEFORE hitting this endpoint so
 * the disallowed branch is captured + logged on the FE rather than
 * surfacing as a generic 4xx.
 *
 * Idempotent — replaying the same (concernId, action, clientRequestId)
 * returns the existing post-transition row.
 */
export async function transitionConcern(
    concernId: string,
    action: ConcernTransitionAction,
    opts: { comment?: string; clientRequestId?: string } = {},
): Promise<ReportDispute> {
    const headers = opts.clientRequestId
        ? { 'X-Idempotency-Key': opts.clientRequestId }
        : undefined;
    const response = await apiClient.patch(
        url(`/api/mentorship/concerns/${concernId}/transition/`),
        { action, ...(opts.comment ? { comment: opts.comment } : {}) },
        headers ? { headers } : undefined,
    );
    // ADR-0002 Phase 1 mutation sweep: WRITE response wrapped via the
    // safeParse boundary helper.
    return parseAtBoundary(ReportDisputeSchema, response.data, {
        op: 'transitionConcern',
    }) as unknown as ReportDispute;
}

/**
 * List the bidirectional state-transition history for a single
 * Concern. Visible to BOTH mentor and mentee on the same Concern row;
 * powers the `ConcernTimeline` inline panel. The endpoint returns the
 * full history (oldest → newest) — pagination is not needed because a
 * single Concern's lifecycle is bounded by the state machine (≤ ~6
 * rows even in the most contested case).
 */
export async function listConcernHistory(
    concernId: string,
    opts: { signal?: AbortSignal } = {},
): Promise<ConcernHistoryEntry[]> {
    const response = await apiClient.get(
        url(`/api/mentorship/concerns/${concernId}/history/`),
        opts.signal ? { signal: opts.signal } : undefined,
    );
    // ADR-0002 Phase 1 Tier B: boundary parse with newly-authored
    // ConcernHistoryEntrySchema on bare-array response.
    return parseAtBoundary(
        ConcernHistoryEntrySchema.array(),
        response.data,
        { op: 'listConcernHistory' },
    ) as unknown as ConcernHistoryEntry[];
}

export async function listReports(
    filters: { mentee_id?: number; module?: ModuleKey; page?: number; signal?: AbortSignal } = {},
): Promise<PagedResponse<Report>> {
    const params = new URLSearchParams();
    if (filters.mentee_id) params.set('mentee_id', String(filters.mentee_id));
    if (filters.module) params.set('module', filters.module);
    if (filters.page) params.set('page', String(filters.page));
    const qs = params.toString();
    const response = await apiClient.get(
        url(`/api/mentorship/reports/${qs ? '?' + qs : ''}`),
        filters.signal ? { signal: filters.signal } : undefined,
    );
    // ADR-0002 Phase 0: validate the `results` array items via the
    // safeParse boundary helper so a serializer drift surfaces as a
    // single typed `ResponseShapeError` instead of a raw ZodError
    // exploding three components downstream. The legacy `{ count, next,
    // previous, results }` envelope itself is passed through
    // structurally — only the items get the schema check.
    const data = response.data as PagedResponse<unknown>;
    const results = parseAtBoundary(ReportSchema.array(), data.results, {
        op: 'listReports',
    }) as unknown as Report[];
    return { ...data, results };
}

export async function getReport(reportId: string): Promise<Report> {
    const response = await apiClient.get(url(`/api/mentorship/reports/${reportId}/`));
    // ADR-0002 Phase 1 Tier A: safeParse boundary helper.
    return parseAtBoundary(ReportSchema, response.data, {
        op: 'getReport',
    }) as unknown as Report;
}

// ────────────────────────────────────────────────────────────────────────
// Insights
// ────────────────────────────────────────────────────────────────────────

export type CreateInsightInput = {
    user_id: number;
    type: InsightType;
    priority: Priority;
    trigger_context: string;
    prompt: string;
};

export async function listInsights(
    filters: {
        user_id?: number;
        type?: InsightType;
        priority?: Priority;
        page?: number;
        signal?: AbortSignal;
    } = {},
): Promise<PagedResponse<Insight>> {
    const params = new URLSearchParams();
    if (filters.user_id) params.set('user_id', String(filters.user_id));
    if (filters.type) params.set('type', filters.type);
    if (filters.priority) params.set('priority', filters.priority);
    if (filters.page) params.set('page', String(filters.page));
    const qs = params.toString();
    const response = await apiClient.get(
        url(`/api/mentorship/insights/${qs ? '?' + qs : ''}`),
        filters.signal ? { signal: filters.signal } : undefined,
    );
    // ADR-0002 Phase 0: safeParse boundary helper — see `listReports`
    // for the rationale. Only the `results` items are validated; the
    // legacy envelope keys pass through unchanged.
    const data = response.data as PagedResponse<unknown>;
    const results = parseAtBoundary(InsightSchema.array(), data.results, {
        op: 'listInsights',
    }) as unknown as Insight[];
    return { ...data, results };
}

export async function createInsight(
    input: CreateInsightInput,
    clientRequestId?: string,
): Promise<Insight> {
    const response = await apiClient.post(
        url('/api/mentorship/insights/'),
        input,
        clientRequestId
            // ADR-0001 §2.1: canonical `X-Idempotency-Key` (prefixed).
            ? { headers: { 'X-Idempotency-Key': clientRequestId } }
            : undefined,
    );
    // ADR-0002 Phase 1 mutation sweep: WRITE response wrapped via the
    // safeParse boundary helper.
    return parseAtBoundary(InsightSchema, response.data, {
        op: 'createInsight',
    }) as unknown as Insight;
}

export async function updateInsight(
    insightId: string,
    partial: Partial<Pick<Insight, 'type' | 'priority' | 'trigger_context' | 'prompt'>>,
): Promise<Insight> {
    const response = await apiClient.patch(url(`/api/mentorship/insights/${insightId}/`), partial);
    // ADR-0002 Phase 1 mutation sweep: WRITE response wrapped via the
    // safeParse boundary helper.
    return parseAtBoundary(InsightSchema, response.data, {
        op: 'updateInsight',
    }) as unknown as Insight;
}

export async function archiveInsight(insightId: string): Promise<void> {
    await apiClient.delete(url(`/api/mentorship/insights/${insightId}/`));
}

export async function recordInsightFeedback(
    insightId: string,
    action: 'accepted' | 'dismissed',
): Promise<Insight> {
    const response = await apiClient.post(
        url(`/api/mentorship/insights/${insightId}/feedback/`),
        { action },
    );
    return response.data;
}

// ────────────────────────────────────────────────────────────────────────
// Client profiles
// ────────────────────────────────────────────────────────────────────────

export type UpsertClientProfileInput = {
    client_id: string;
    priorities: string[];
    behaviors: string[];
    management_advice: string[];
    visibility?: ClientProfileVisibility;
};

export async function listClientProfiles(
    filters: { client_id?: string; page?: number; signal?: AbortSignal } = {},
): Promise<PagedResponse<ClientProfile>> {
    const params = new URLSearchParams();
    if (filters.client_id) params.set('client_id', filters.client_id);
    if (filters.page) params.set('page', String(filters.page));
    const qs = params.toString();
    const response = await apiClient.get(
        url(`/api/mentorship/client_profiles/${qs ? '?' + qs : ''}`),
        filters.signal ? { signal: filters.signal } : undefined,
    );
    // ADR-0002 Phase 0: safeParse boundary helper — items validated;
    // legacy envelope passes through structurally.
    const data = response.data as PagedResponse<unknown>;
    const results = parseAtBoundary(ClientProfileSchema.array(), data.results, {
        op: 'listClientProfiles',
    }) as unknown as ClientProfile[];
    return { ...data, results };
}

export async function upsertClientProfile(
    input: UpsertClientProfileInput,
    clientRequestId?: string,
): Promise<ClientProfile> {
    const response = await apiClient.post(
        url('/api/mentorship/client_profiles/'),
        input,
        clientRequestId
            // ADR-0001 §2.1: canonical `X-Idempotency-Key` (prefixed).
            ? { headers: { 'X-Idempotency-Key': clientRequestId } }
            : undefined,
    );
    return response.data;
}

export async function getClientProfile(profileId: string): Promise<ClientProfile> {
    const response = await apiClient.get(url(`/api/mentorship/client_profiles/${profileId}/`));
    // ADR-0002 Phase 1 Tier B: boundary parse with ClientProfileSchema.
    return parseAtBoundary(ClientProfileSchema, response.data, {
        op: 'getClientProfile',
    }) as unknown as ClientProfile;
}

export async function updateClientProfile(
    profileId: string,
    partial: Partial<UpsertClientProfileInput>,
): Promise<ClientProfile> {
    const response = await apiClient.patch(
        url(`/api/mentorship/client_profiles/${profileId}/`),
        partial,
    );
    return response.data;
}

export async function deleteClientProfile(profileId: string): Promise<void> {
    await apiClient.delete(url(`/api/mentorship/client_profiles/${profileId}/`));
}

// ────────────────────────────────────────────────────────────────────────
// MentorshipProject
// ────────────────────────────────────────────────────────────────────────

export type CreateMentorshipProjectInput = {
    name: string;
    mentee_id: number;
    mentor_id?: number | null | undefined;
    client_id?: string | undefined;
    description?: string | undefined;
    start_date?: string | null | undefined;
    end_date?: string | null | undefined;
};

export type UpdateMentorshipProjectInput = Partial<
    Pick<
        MentorshipProject,
        'name' | 'client_id' | 'description' | 'start_date' | 'end_date'
    >
>;

export async function listMentorshipProjects(
    filters: {
        mentee_id?: number;
        mentor_id?: number;
        client_id?: string;
        active?: boolean;
        page?: number;
        signal?: AbortSignal;
    } = {},
): Promise<PagedResponse<MentorshipProject>> {
    const params = new URLSearchParams();
    if (filters.mentee_id) params.set('mentee_id', String(filters.mentee_id));
    if (filters.mentor_id) params.set('mentor_id', String(filters.mentor_id));
    if (filters.client_id) params.set('client_id', filters.client_id);
    if (filters.active === false) params.set('active', '0');
    if (filters.page) params.set('page', String(filters.page));
    const qs = params.toString();
    const response = await apiClient.get(
        url(`/api/mentorship/projects/${qs ? '?' + qs : ''}`),
        filters.signal ? { signal: filters.signal } : undefined,
    );
    // ADR-0002 Phase 0: safeParse boundary helper — items validated;
    // legacy envelope passes through structurally.
    const data = response.data as PagedResponse<unknown>;
    const results = parseAtBoundary(MentorshipProjectSchema.array(), data.results, {
        op: 'listMentorshipProjects',
    }) as unknown as MentorshipProject[];
    return { ...data, results };
}

export async function createMentorshipProject(
    input: CreateMentorshipProjectInput,
    clientRequestId?: string,
): Promise<MentorshipProject> {
    const response = await apiClient.post(
        url('/api/mentorship/projects/'),
        input,
        clientRequestId
            // ADR-0001 §2.1: canonical `X-Idempotency-Key` (prefixed).
            ? { headers: { 'X-Idempotency-Key': clientRequestId } }
            : undefined,
    );
    return response.data;
}

export async function getMentorshipProject(projectId: string): Promise<MentorshipProject> {
    const response = await apiClient.get(url(`/api/mentorship/projects/${projectId}/`));
    // ADR-0002 Phase 1 Tier B: boundary parse with MentorshipProjectSchema.
    return parseAtBoundary(MentorshipProjectSchema, response.data, {
        op: 'getMentorshipProject',
    }) as unknown as MentorshipProject;
}

export async function updateMentorshipProject(
    projectId: string,
    partial: UpdateMentorshipProjectInput,
): Promise<MentorshipProject> {
    const response = await apiClient.patch(
        url(`/api/mentorship/projects/${projectId}/`),
        partial,
    );
    return response.data;
}

export async function archiveMentorshipProject(
    projectId: string,
    reason: string = '',
): Promise<MentorshipProject> {
    const response = await apiClient.post(
        url(`/api/mentorship/projects/${projectId}/archive/`),
        { reason },
    );
    return response.data;
}

export async function unarchiveMentorshipProject(
    projectId: string,
    reason: string = '',
): Promise<MentorshipProject> {
    const response = await apiClient.post(
        url(`/api/mentorship/projects/${projectId}/unarchive/`),
        { reason },
    );
    return response.data;
}

export async function reassignMentorshipProjectMentor(
    projectId: string,
    newMentorId: number | null,
    reason: string,
): Promise<MentorshipProject> {
    const response = await apiClient.patch(
        url(`/api/mentorship/projects/${projectId}/reassign_mentor/`),
        { mentor_id: newMentorId, reason },
    );
    return response.data;
}

export async function deleteMentorshipProject(projectId: string): Promise<void> {
    await apiClient.delete(url(`/api/mentorship/projects/${projectId}/`));
}

export async function getMentorshipProjectRisks(projectId: string): Promise<Risk[]> {
    const response = await apiClient.get(
        url(`/api/mentorship/projects/${projectId}/risks/`),
    );
    return response.data;
}

// ────────────────────────────────────────────────────────────────────────
// Risks
// ────────────────────────────────────────────────────────────────────────

export type UpsertRiskInput = {
    project_id: string;
    trigger: string;
    impact: string;
    mitigation: string;
    priority: Priority;
};

export async function listRisks(
    projectId: string,
    filters: { active?: boolean; priority?: Priority; page?: number; signal?: AbortSignal } = {},
): Promise<PagedResponse<Risk>> {
    const params = new URLSearchParams();
    params.set('project_id', projectId);
    if (filters.active === false) params.set('active', '0');
    if (filters.priority) params.set('priority', filters.priority);
    if (filters.page) params.set('page', String(filters.page));
    const response = await apiClient.get(
        url(`/api/mentorship/risks/?${params.toString()}`),
        filters.signal ? { signal: filters.signal } : undefined,
    );
    // ADR-0002 Phase 1 Tier B: safeParse boundary helper — items
    // validated via RiskSchema; legacy envelope keys (count/next/previous)
    // pass through structurally. Same posture as `listReports`/
    // `listInsights` Phase 0 wraps.
    const data = response.data as PagedResponse<unknown>;
    const results = parseAtBoundary(RiskSchema.array(), data.results, {
        op: 'listRisks',
    }) as unknown as Risk[];
    return { ...data, results };
}

export async function createRisk(
    input: UpsertRiskInput,
    clientRequestId?: string,
): Promise<Risk> {
    const response = await apiClient.post(
        url('/api/mentorship/risks/'),
        input,
        clientRequestId
            // ADR-0001 §2.1: canonical `X-Idempotency-Key` (prefixed).
            ? { headers: { 'X-Idempotency-Key': clientRequestId } }
            : undefined,
    );
    return response.data;
}

export async function getRisk(riskId: string): Promise<Risk> {
    const response = await apiClient.get(url(`/api/mentorship/risks/${riskId}/`));
    // ADR-0002 Phase 1 Tier B: boundary parse with newly-authored RiskSchema.
    return parseAtBoundary(RiskSchema, response.data, {
        op: 'getRisk',
    }) as unknown as Risk;
}

export async function updateRisk(
    riskId: string,
    partial: Partial<Omit<UpsertRiskInput, 'project_id'>>,
): Promise<Risk> {
    const response = await apiClient.patch(url(`/api/mentorship/risks/${riskId}/`), partial);
    return response.data;
}

export async function archiveRisk(riskId: string): Promise<void> {
    await apiClient.delete(url(`/api/mentorship/risks/${riskId}/`));
}

// ────────────────────────────────────────────────────────────────────────
// Engagement summaries (entity #8)
// ────────────────────────────────────────────────────────────────────────

export async function getEngagementSummary(
    userId: number,
    periodStart: string,
    periodEnd: string,
    persist: boolean = false,
    opts: { signal?: AbortSignal } = {},
): Promise<EngagementSummary | EngagementSummarySnapshot> {
    const params = new URLSearchParams({
        user_id: String(userId),
        period_start: periodStart,
        period_end: periodEnd,
    });
    if (persist) params.set('persist', '1');
    const response = await apiClient.get(
        url(`/api/mentorship/engagement_summaries/?${params.toString()}`),
        opts.signal ? { signal: opts.signal } : undefined,
    );
    return response.data;
}

export async function listEngagementSummarySnapshots(
    userId: number,
): Promise<PagedResponse<EngagementSummarySnapshot>> {
    const response = await apiClient.get(
        url(`/api/mentorship/engagement_summaries/snapshots/?user_id=${userId}`),
    );
    return response.data;
}

// ────────────────────────────────────────────────────────────────────────
// Learning updates (entity #9)
// ────────────────────────────────────────────────────────────────────────

export async function listLearningUpdates(
    page: number = 1,
): Promise<PagedResponse<LearningUpdate>> {
    const response = await apiClient.get(
        url(`/api/mentorship/learning_updates/?page=${page}`),
    );
    return response.data;
}

export async function runLearningUpdate(
    periodStart?: string,
    periodEnd?: string,
): Promise<LearningUpdate> {
    const body: Record<string, string> = {};
    if (periodStart) body.period_start = periodStart;
    if (periodEnd) body.period_end = periodEnd;
    const response = await apiClient.post(url('/api/mentorship/learning_updates/run/'), body);
    return response.data;
}

export async function applyLearningUpdate(updateId: string): Promise<LearningUpdate> {
    const response = await apiClient.post(
        url(`/api/mentorship/learning_updates/${updateId}/apply/`),
    );
    return response.data;
}

// ────────────────────────────────────────────────────────────────────────
// Notifications: see `notificationsService.ts`
// ────────────────────────────────────────────────────────────────────────
//
// Notification helpers live in `src/notificationsService.ts` (single
// SSOT for both mentorship and networking). Import from there:
//
//   import { listNotifications, markNotificationRead,
//            markAllNotificationsRead, getUnreadSummary }
//   from './notificationsService';

// ────────────────────────────────────────────────────────────────────────
// Role management
// ────────────────────────────────────────────────────────────────────────

export type CreateRoleRequestPayload = {
    target_modules?: ModuleKey[];
    specialties?: ModuleKey[];
    seniority?: 'Junior' | 'Mid' | 'Senior';
    bio?: string;
    linkedin?: string;
    desired_mentor_id?: number;
    reason?: string;
};

export async function createRoleRequest(
    requestType: RoleRequestType,
    payload: CreateRoleRequestPayload,
    clientRequestId?: string,
): Promise<RoleRequest> {
    const response = await apiClient.post(
        url('/api/mentorship/role_requests/'),
        { request_type: requestType, payload },
        clientRequestId
            // ADR-0001 §2.1: canonical `X-Idempotency-Key` (prefixed).
            ? { headers: { 'X-Idempotency-Key': clientRequestId } }
            : undefined,
    );
    return response.data;
}

export async function listRoleRequests(
    state?: RoleRequestState,
): Promise<PagedResponse<RoleRequest>> {
    const params = state ? `?state=${state}` : '';
    const response = await apiClient.get(url(`/api/mentorship/role_requests/${params}`));
    return response.data;
}

export async function approveRoleRequest(
    requestId: string,
    reason: string = '',
): Promise<RoleRequest> {
    const response = await apiClient.post(
        url(`/api/mentorship/role_requests/${requestId}/approve/`),
        { reason },
    );
    return response.data;
}

export async function rejectRoleRequest(
    requestId: string,
    reason: string,
): Promise<RoleRequest> {
    const response = await apiClient.post(
        url(`/api/mentorship/role_requests/${requestId}/reject/`),
        { reason },
    );
    return response.data;
}

export type SetUserRoleInput = {
    new_role: 'mentor' | 'mentee' | null;
    reason: string;
    mentor_id?: number | null | undefined;
    mentor_specialties?: ModuleKey[] | undefined;
    mentor_capacity?: number | undefined;
};

export async function listUsers(
    filters: {
        role?: RoleKey | undefined;
        search?: string | undefined;
        page?: number | undefined;
        signal?: AbortSignal | undefined;
    } = {},
): Promise<PagedResponse<UserSummary>> {
    const params = new URLSearchParams();
    if (filters.role) params.set('role', filters.role);
    if (filters.search) params.set('search', filters.search);
    if (filters.page) params.set('page', String(filters.page));
    const qs = params.toString();
    const response = await apiClient.get(
        url(`/api/mentorship/users/${qs ? '?' + qs : ''}`),
        filters.signal ? { signal: filters.signal } : undefined,
    );
    return response.data;
}

export async function setUserRole(
    userId: number,
    input: SetUserRoleInput,
): Promise<{
    role: RoleKey;
    mentor_id: number | null;
    mentor_specialties: ModuleKey[];
    mentor_capacity: number;
}> {
    const response = await apiClient.patch(
        url(`/api/mentorship/users/${userId}/role/`),
        input,
    );
    return response.data;
}

export async function setUserMentor(
    userId: number,
    mentorId: number | null,
    reason: string,
): Promise<{ mentor_id: number | null }> {
    const response = await apiClient.patch(url(`/api/mentorship/users/${userId}/mentor/`), {
        mentor_id: mentorId,
        reason,
    });
    return response.data;
}

export async function setUserCapacity(
    userId: number,
    mentorCapacity: number,
    reason: string,
): Promise<{ mentor_capacity: number }> {
    const response = await apiClient.patch(
        url(`/api/mentorship/users/${userId}/capacity/`),
        { mentor_capacity: mentorCapacity, reason },
    );
    return response.data;
}

export async function setUserSpecialties(
    userId: number,
    specialties: ModuleKey[],
    reason: string,
): Promise<{ mentor_specialties: ModuleKey[] }> {
    const response = await apiClient.patch(
        url(`/api/mentorship/users/${userId}/specialties/`),
        { mentor_specialties: specialties, reason },
    );
    return response.data;
}

export async function getUserAuditLog(
    userId: number,
    limit: number = 50,
): Promise<RoleAuditLogRow[]> {
    const response = await apiClient.get(
        url(`/api/mentorship/users/${userId}/audit_log/?limit=${limit}`),
    );
    return response.data;
}

// ────────────────────────────────────────────────────────────────────────
// Meeting (F1 — framework-evaluation catchup schedule)
// ────────────────────────────────────────────────────────────────────────

export type MeetingCadence =
    | 'weekly' | 'fortnightly' | 'monthly' | 'quarterly' | 'ad_hoc';

export type MeetingStatus =
    | 'scheduled' | 'occurred' | 'missed' | 'cancelled';

export type MeetingHistoryEntry = {
    at: string;            // ISO-8601
    actor_id: number;
    action: string;        // 'created' | 'rescheduled' | 'marked_occurred' | etc.
    reason: string;
};

export type Meeting = {
    id: string;
    mentor: number;
    mentee: number;
    mentor_summary: UserMini | null;
    mentee_summary: UserMini | null;
    next_meeting_at: string;  // ISO-8601
    cadence: MeetingCadence;
    status: MeetingStatus;
    duration_minutes: number;
    location: string;
    notes: string;
    history: MeetingHistoryEntry[];
    created_at: string;
    updated_at: string;
};

export async function listMeetings(
    filters: { mentorId?: number; menteeId?: number; page?: number } = {},
): Promise<PagedResponse<Meeting>> {
    const params = new URLSearchParams();
    if (filters.mentorId) params.set('mentor_id', String(filters.mentorId));
    if (filters.menteeId) params.set('mentee_id', String(filters.menteeId));
    if (filters.page) params.set('page', String(filters.page));
    const qs = params.toString();
    const response = await apiClient.get(
        url(`/api/mentorship/meetings/${qs ? '?' + qs : ''}`),
    );
    return response.data;
}

/** Filter helper — returns the subset of `meetings` whose
 *  ``next_meeting_at`` falls within the next ``windowDays`` from
 *  ``now`` (defaults to 7). Filters out cancelled rows so the
 *  upcoming strip never offers to "take over" a cancelled meeting.
 *  Sorted earliest-first; ties broken by meeting id so the order is
 *  deterministic across renders.
 *
 *  Exposed alongside {@link listUpcomingMeetings} so callers that
 *  already hold a `Meeting[]` (test seeds, parent-fetched lists) can
 *  filter without a second network round-trip. */
export function filterUpcomingMeetings(
    meetings: ReadonlyArray<Meeting>,
    now: number = Date.now(),
    windowDays: number = 7,
): Meeting[] {
    const windowMs = windowDays * 24 * 60 * 60 * 1000;
    const horizon = now + windowMs;
    const out: Meeting[] = [];
    for (const m of meetings) {
        if (m.status === 'cancelled') continue;
        const ts = new Date(m.next_meeting_at).getTime();
        // Defensive: malformed `next_meeting_at` → NaN → skip. Same
        // posture as `categorizeWaitingForMentee`'s NaN guard.
        if (!Number.isFinite(ts)) continue;
        if (ts < now || ts > horizon) continue;
        out.push(m);
    }
    // Earliest first; stable tiebreak on id so two meetings at the
    // same minute always render in the same order.
    out.sort((a, b) => {
        const ta = new Date(a.next_meeting_at).getTime();
        const tb = new Date(b.next_meeting_at).getTime();
        if (ta !== tb) return ta - tb;
        return a.id.localeCompare(b.id);
    });
    return out;
}

/** Convenience wrapper that pages through {@link listMeetings} and
 *  returns just the meetings inside the next ``windowDays`` window
 *  (default 7) for the given mentor.
 *
 *  Why a wrapper: the THIS WEEK strip in `NeedsAttentionView` cares
 *  about a tiny window, but the underlying endpoint is paged + filtered
 *  by mentor/mentee, not by date. Keeping the window logic here (one
 *  filter + sort pass) means consumers don't reinvent the bounds. */
export async function listUpcomingMeetings(
    filters: { mentorId?: number; windowDays?: number; now?: number } = {},
): Promise<Meeting[]> {
    const { mentorId, windowDays = 7, now = Date.now() } = filters;
    const page = await listMeetings(
        mentorId !== undefined ? { mentorId } : {},
    );
    return filterUpcomingMeetings(page.results, now, windowDays);
}

/** Deterministic per-pair lookup. Returns the nearest upcoming
 *  scheduled meeting for the (mentor, mentee) pair, or ``null`` when
 *  the backend has no upcoming scheduled row (404). Network / 5xx
 *  errors propagate as raw rejections so the caller can fall back to
 *  the list endpoint.
 *
 *  Implementation note: the backend deliberately returns 404 (NOT 200
 *  with null) when no upcoming scheduled meeting exists. We translate
 *  that single sentinel here so the calling component branches on a
 *  ``null`` value rather than a status code. */
export async function getMeetingForPair(
    mentorId: number,
    menteeId: number,
): Promise<Meeting | null> {
    const params = new URLSearchParams();
    params.set('mentor', String(mentorId));
    params.set('mentee', String(menteeId));
    try {
        const response = await apiClient.get(
            url(`/api/mentorship/meeting-for-pair/?${params.toString()}`),
        );
        return response.data as Meeting;
    } catch (err: unknown) {
        // Distinguish "no upcoming scheduled meeting" (404 — clean
        // signal, caller renders the empty state OR falls back to a
        // pre-loaded list) from "transport failure" (5xx, network,
        // 403/401). Only the former collapses to null; everything else
        // propagates so the caller can decide how to fall back.
        const status =
            (err as { response?: { status?: number } } | null | undefined)
                ?.response?.status;
        if (status === 404) return null;
        throw err;
    }
}

export type CreateMeetingInput = {
    mentor: number;
    mentee: number;
    next_meeting_at: string;   // ISO-8601 (UTC or with offset)
    cadence?: MeetingCadence;
    duration_minutes?: number;
    location?: string;
    notes?: string;
};

export async function createMeeting(
    input: CreateMeetingInput,
    clientRequestId?: string,
): Promise<Meeting> {
    const response = await apiClient.post(
        url('/api/mentorship/meetings/'),
        input,
        clientRequestId
            // ADR-0001 §2.1: canonical `X-Idempotency-Key` (prefixed).
            ? { headers: { 'X-Idempotency-Key': clientRequestId } }
            : undefined,
    );
    // ADR-0002 Phase 1 mutation sweep: WRITE response wrapped via the
    // safeParse boundary helper. MeetingSchema is newly authored for
    // this slice (see `src/schemas/mentorship.ts`).
    return parseAtBoundary(MeetingSchema, response.data, {
        op: 'createMeeting',
    }) as unknown as Meeting;
}

export async function getMeeting(meetingId: string): Promise<Meeting> {
    const response = await apiClient.get(url(`/api/mentorship/meetings/${meetingId}/`));
    return response.data;
}

export type UpdateMeetingInput = Partial<{
    next_meeting_at: string;
    cadence: MeetingCadence;
    duration_minutes: number;
    location: string;
    notes: string;
    reason: string;   // free-text audit-trail reason for the reschedule
}>;

export async function updateMeeting(
    meetingId: string,
    patch: UpdateMeetingInput,
): Promise<Meeting> {
    const response = await apiClient.patch(
        url(`/api/mentorship/meetings/${meetingId}/`),
        patch,
    );
    return response.data;
}

export async function markMeetingOccurred(
    meetingId: string,
    reason: string = '',
): Promise<Meeting> {
    const response = await apiClient.post(
        url(`/api/mentorship/meetings/${meetingId}/mark_occurred/`),
        { reason },
    );
    return response.data;
}

export async function markMeetingMissed(
    meetingId: string,
    reason: string = '',
): Promise<Meeting> {
    const response = await apiClient.post(
        url(`/api/mentorship/meetings/${meetingId}/mark_missed/`),
        { reason },
    );
    return response.data;
}

export async function cancelMeeting(
    meetingId: string,
    reason: string = '',
): Promise<Meeting> {
    const response = await apiClient.post(
        url(`/api/mentorship/meetings/${meetingId}/cancel/`),
        { reason },
    );
    return response.data;
}

/** Build a URL that the browser can navigate-to for an .ics download.
 *  The endpoint is session-authed so a plain `<a href>` or
 *  `window.location.href` works without the axios auth headers. */
export function meetingIcsUrl(meetingId: string): string {
    return url(`/api/mentorship/meetings/${meetingId}/ics/`);
}

// ────────────────────────────────────────────────────────────────────────
// WS-M-4 — one-click scheduler additions
// ────────────────────────────────────────────────────────────────────────
//
// These functions are ADDITIVE — they wrap the same meetings endpoints
// the WS-M-1 sweep already migrated to `X-Idempotency-Key`, but expose
// a higher-level "click a pill, confirm a slot" shape consumed by the
// SchedulerOneClickPanel.
//
// scheduleOneClick(menteeId, slot):
//   - One-shot create-or-update for the (mentor, mentee) pair scoped to
//     a pre-filled slot. The pair-uniqueness invariant is enforced by
//     the backend (Tier 1 viewset); the FE simply POSTs the pre-filled
//     payload and lets the dedupe table collapse retries.
//   - ADR-0001 §2.1 canonical `X-Idempotency-Key` header — the second
//     argument MUST be the key the caller's `useIdempotentMutation`
//     minted (replays survive retries).
//   - NO unprefixed legacy header. The backend ingress rejects the
//     `Idempotency-Key` form outright.
//
// listMeetingHistory(menteeId, cursor?):
//   - Cursor-paginated reader for the MeetingHistoryDrawer. Wraps the
//     same `/api/mentorship/meetings/?mentee_id=...&cursor=...` shape
//     used by the existing list reader. Returns `{ items, nextCursor }`
//     so the drawer can implement load-more without owning the envelope
//     shape. The wire `next_cursor` may legitimately be absent on the
//     legacy page envelope (count/next/previous) — fall back to `null`
//     so the drawer hides Load-more for that branch.

export type OneClickSlotInput = {
    /** ISO-8601 (UTC or offset). The pre-filled slot the mentor picked. */
    next_meeting_at: string;
    /** Cadence label baked into the meeting row. Defaults to 'ad_hoc'
     *  on the backend if omitted — explicit here so the row reflects
     *  the panel's per-pill suggestion (always 'ad_hoc' for the first
     *  three suggested slots). */
    cadence?: MeetingCadence;
    /** Duration in minutes. Defaults to 30 on the backend if omitted. */
    duration_minutes?: number;
    /** Optional free-text location (link, room, "TBD"). */
    location?: string;
    /** Optional free-text mentor note attached at create time. */
    notes?: string;
};

/** One-shot mentor-side confirm of a pre-filled slot pill. Wraps
 *  `createMeeting` with a fixed mentor side (the caller supplies the
 *  mentor id) so SchedulerOneClickPanel never has to thread the create
 *  payload itself.
 *
 *  ADR-0001 §2.1 contract: the `idempotencyKey` is the `X-Idempotency-Key`
 *  header value. Callers MUST mint it via `useIdempotentMutation` so a
 *  retry replays the same key and the backend dedupe table collapses
 *  duplicate confirms into one Meeting row. */
export async function scheduleOneClick(
    args: { mentorId: number; menteeId: number; slot: OneClickSlotInput },
    idempotencyKey: string,
): Promise<Meeting> {
    const { mentorId, menteeId, slot } = args;
    const body: CreateMeetingInput = {
        mentor: mentorId,
        mentee: menteeId,
        next_meeting_at: slot.next_meeting_at,
        ...(slot.cadence !== undefined ? { cadence: slot.cadence } : {}),
        ...(slot.duration_minutes !== undefined
            ? { duration_minutes: slot.duration_minutes }
            : {}),
        ...(slot.location !== undefined ? { location: slot.location } : {}),
        ...(slot.notes !== undefined ? { notes: slot.notes } : {}),
    };
    const response = await apiClient.post(
        url('/api/mentorship/meetings/'),
        body,
        // ADR-0001 §2.1: canonical `X-Idempotency-Key` (prefixed). The
        // unprefixed legacy form is rejected at backend ingress.
        { headers: { 'X-Idempotency-Key': idempotencyKey } },
    );
    return response.data as Meeting;
}

/** Cursor-paginated history reader for the (mentor-view) meeting drawer.
 *  Returns past + upcoming meeting rows for the mentee, ordered by the
 *  backend's default sort (most-recent first for occurred / cancelled;
 *  scheduled meetings sit at the top of the next page). `cursor` is an
 *  opaque token the backend mints on the prior page's `next_cursor`. */
export async function listMeetingHistory(
    menteeId: number,
    opts: { cursor?: string | null; signal?: AbortSignal } = {},
): Promise<CursorPage<Meeting>> {
    const params = new URLSearchParams();
    params.set('mentee_id', String(menteeId));
    if (opts.cursor) params.set('cursor', opts.cursor);
    const response = await apiClient.get(
        url(`/api/mentorship/meetings/?${params.toString()}`),
        opts.signal ? { signal: opts.signal } : undefined,
    );
    // The meetings endpoint serves both the legacy `{count, next,
    // previous, results}` envelope (when paginated by DRF default) and
    // the cursor envelope (post-WS-ENT-2). Read `next_cursor` when the
    // backend provides it; otherwise fall back to `null` so the drawer
    // hides its Load-more affordance on the legacy branch. The wire
    // shape is structurally parsed here (the legacy `Meeting` type is
    // already authoritative for this endpoint).
    const raw = response.data as {
        results?: Meeting[];
        next_cursor?: string | null;
        count?: number | null;
    };
    return {
        items: Array.isArray(raw.results) ? raw.results : [],
        nextCursor:
            typeof raw.next_cursor === 'string' && raw.next_cursor.length > 0
                ? raw.next_cursor
                : null,
        totalCount: typeof raw.count === 'number' ? raw.count : null,
    };
}

// ────────────────────────────────────────────────────────────────────────
// Mentality overlay (M3 + M4)
// ────────────────────────────────────────────────────────────────────────

export type MentalityContext = {
    user_id: number;
    module: ModuleKey | null;
    client_profiles: ClientProfile[];
    risks: Risk[];
    insights: Insight[];
    generated_at: string;
};

export async function getMentalityContext(
    module?: ModuleKey,
    /** Focused-entity id (e.g. ``mentee::42``) so the backend can
     *  re-rank insights/recommendations for the focused mentee. When
     *  omitted the call falls back to global ranking. The id is treated
     *  as opaque on the frontend — the backend parses the
     *  ``<kind>::<wireId>`` shape. */
    focusId?: string,
): Promise<MentalityContext> {
    const params = new URLSearchParams();
    if (module) params.set('module', module);
    if (focusId) params.set('focus', focusId);
    const qs = params.toString();
    const response = await apiClient.get(
        url(`/api/mentorship/mentality_context/${qs ? '?' + qs : ''}`),
    );
    return response.data;
}

export type MentalityChatRuleRef = {
    id: string;
    kind: 'risk' | 'insight' | 'profile';
};

export type MentalityChatResponse = {
    answer: string;
    rules: MentalityChatRuleRef[];
    followup: string;
    meta: {
        model: string;
        latency_ms: number;
        token_usage?: Record<string, number>;
    };
};

export type MentalityChatInput = {
    query: string;
    module?: ModuleKey | undefined;
    /** M4 — optional doc metadata. `documentText` is capped at 8000
     *  chars server-side. */
    documentName?: string | undefined;
    documentText?: string | undefined;
};

export async function postMentalityChat(
    input: MentalityChatInput,
): Promise<MentalityChatResponse> {
    const response = await apiClient.post(
        url('/api/mentorship/mentality_chat/'),
        {
            query: input.query,
            module: input.module ?? null,
            document_name: input.documentName ?? '',
            document_text: input.documentText ?? '',
        },
    );
    return response.data;
}

export type MentalityDocumentUploadResponse = {
    document_name: string;
    document_text: string;
    truncated: boolean;
    bytes: number;
};

/** Upload a PDF/DOCX/text file; the backend extracts the text and
 *  returns a payload the caller can feed straight into postMentalityChat.
 *  Also persists the extraction as the user's current attachment. */
export async function uploadMentalityDocument(
    file: File,
): Promise<MentalityDocumentUploadResponse> {
    const body = new FormData();
    body.append('file', file);
    const response = await apiClient.post(
        url('/api/mentorship/mentality_chat/upload/'),
        body,
        {
            headers: { 'Content-Type': 'multipart/form-data' },
        },
    );
    return response.data;
}

export type MentalityDocumentSnapshot = {
    document_name: string;
    document_text: string;
    truncated: boolean;
    bytes: number;
    updated_at: string | null;
};

/** Fetch the user's persisted attachment. Returns an empty shape when
 *  nothing is saved — never throws 404. */
export async function getMentalityDocument(): Promise<MentalityDocumentSnapshot> {
    const response = await apiClient.get(url('/api/mentorship/mentality_document/'));
    return response.data;
}

/** Upsert the pasted document name/text. Used when the mentee edits
 *  inline rather than uploading a file. */
export async function putMentalityDocument(
    input: { documentName: string; documentText: string },
): Promise<MentalityDocumentSnapshot> {
    const response = await apiClient.put(
        url('/api/mentorship/mentality_document/'),
        { document_name: input.documentName, document_text: input.documentText },
    );
    return response.data;
}

/** Clear the user's persisted attachment. */
export async function deleteMentalityDocument(): Promise<void> {
    await apiClient.delete(url('/api/mentorship/mentality_document/'));
}

export type MentalityChatTurnPersisted = {
    id: string;
    role: 'user' | 'assistant';
    text: string;
    rule_refs: MentalityChatRuleRef[];
    module: string;
    document_name: string;
    created_at: string;
};

export async function getMentalityChatHistory(): Promise<MentalityChatTurnPersisted[]> {
    const response = await apiClient.get(url('/api/mentorship/mentality_chat/history/'));
    return response.data.turns ?? [];
}

export async function clearMentalityChatHistory(): Promise<void> {
    await apiClient.delete(url('/api/mentorship/mentality_chat/history/'));
}

// ────────────────────────────────────────────────────────────────────────
// Pairing status (WS-FTUE-3 — backend commit 8bf52b52)
// ────────────────────────────────────────────────────────────────────────
//
// GET /api/mentorship/pairing/status/ — the mentee's current pairing-flow
// state, used by the FE PairingCard stepper. The wire shape is
//
//   { stage: 1|2|3|4, queuePosition?: number, mentor?: { id, name } }
//
// Optional fields are OMITTED when absent (not null). The schema mirrors
// that posture so a null-on-wire from a contract drift surfaces as a
// `ResponseShapeError` instead of being coerced into an empty state.
//
// The FE-facing structural type is re-stated here (rather than re-derived
// from the schema) for parity with the rest of this module: hand-rolled
// TS types are the structural SSOT for component code, schemas are the
// runtime guard at the boundary.

/** Minimal mentor projection inlined on the pairing-status response.
 *  Mirrors `PairingMentor` in modules/mentorship/pairing/usePairingStatus.ts. */
export type PairingMentorProjection = {
    id: number;
    name: string;
};

/** Pairing-flow state returned by GET /api/mentorship/pairing/status/.
 *  Optional fields stay optional under exactOptionalPropertyTypes —
 *  consumers narrow via TS rather than `??` guards. */
export type PairingStatusResponse = {
    stage: 1 | 2 | 3 | 4;
    queuePosition?: number;
    mentor?: PairingMentorProjection;
};

/**
 * Fetch the mentee's pairing-flow status. Throws on network failure or
 * on a `ResponseShapeError` if the backend payload does not match the
 * documented contract — the caller (usePairingStatus) catches both and
 * folds them into its `error` state via the SSOT captureError site.
 *
 * No params: the backend infers the mentee from the authed session.
 */
export async function getPairingStatus(): Promise<PairingStatusResponse> {
    const response = await apiClient.get(url('/api/mentorship/pairing/status/'));
    const parsed = parseAtBoundary(PairingStatusSchema, response.data, {
        op: 'getPairingStatus',
    }) as PairingStatusWire;
    // The hand-rolled FE type and the schema-inferred type are
    // structurally identical (both gate `stage` on the same literal
    // union, both make `queuePosition` / `mentor` bare optional). The
    // cast is a one-way upgrade from the boundary-guard type to the
    // service-SSOT type that component code imports.
    return parsed as PairingStatusResponse;
}

// ────────────────────────────────────────────────────────────────────────
// Active task brief (slice 105.10 Phase 2)
// ────────────────────────────────────────────────────────────────────────
//
// GET /api/mentorship/active_task/?module=&quarter= — the cohort's seeded
// Task row for the mentee's current module + quarter. The hook
// `useActiveTaskBrief` is the only consumer today; routing the apiClient
// call through this service preserves the project's no-restricted-imports
// rule (apiClient only from `*service.ts` files) and keeps a single seam
// for future schema/parser wiring.
//
// Wire shape — matches the BE TaskSerializer exactly:
//   { id, module, competency: { title, body }, brief: { title, body, image? },
//     tags: string[], stats: { avgScorePct?, progress: { done, total } },
//     hintCost?, exampleResponse? }
//
// No Zod schema yet — schemas land in the parallel slice. Until then the
// hand-rolled TS type is the boundary contract; a contract drift will
// surface as a render-time failure in MenteeActiveTask rather than at
// the network boundary.

/** Wire shape for the active-task brief. Mirrors the FE-local
 *  `ActiveTask` in `modules/mentorship/MenteeActiveTask/types.ts`. */
export type ActiveTaskBrief = {
    id: string;
    module: ModuleKey;
    competency: { title: string; body: string };
    brief: { title: string; body: string; image?: string };
    tags: string[];
    stats: {
        avgScorePct?: number;
        progress: { done: number; total: number };
    };
    hintCost?: number;
    exampleResponse?: string;
};

/**
 * Resolve the mentee's active Task for the given module + quarter.
 *
 * Quarter is supplied by the caller as a `YYYY-Qn` string — matches the
 * BE seeded shape (TaskSerializer.quarter field).
 *
 * Throws on 404 (no seeded row) and on any transport failure. The
 * `useActiveTaskBrief` hook catches both and surfaces an explicit
 * empty/error state — no silent mock fallback.
 */
export async function getActiveTaskBrief(
    module: ModuleKey,
    quarter: string,
    options: { signal?: AbortSignal } = {},
): Promise<ActiveTaskBrief> {
    const response = await apiClient.get(
        url('/api/mentorship/active_task/'),
        {
            params: { module, quarter },
            ...(options.signal ? { signal: options.signal } : {}),
        },
    );
    return response.data as ActiveTaskBrief;
}

// ────────────────────────────────────────────────────────────────────────
// Next-step resolver (atomic state-machine edge)
// ────────────────────────────────────────────────────────────────────────
//
// GET /api/mentorship/submissions/<submissionId>/next_task/ — resolves
// the deterministic "next thing" the mentee should work on after a
// submission is finalized. The wire shape is a discriminated union keyed
// by `kind`:
//
//   - { kind: 'next_task', task: ActiveTaskBrief }
//       The first (module, quarter) pair the mentee has NOT submitted,
//       walking MODULE_ORDER × seeded Task quarters. Returned verbatim
//       from `TaskSerializer` on the backend so the shape matches the
//       active-task brief.
//   - { kind: 'cohort_complete' }
//       Every seeded pair has a submission for this mentee. Terminal
//       state — the consumer should stop opening MenteeActiveTask.
//   - { kind: 'awaiting_review', submissionId: string }
//       Submission is still SUBMITTED or ASSESSED (mentor has not
//       finalized yet). The caller polls.
//
// HARD determinism contract: the BE rule is a pure function of
// already-persisted rows (Submission + Task), so the same submission
// id returns byte-identical JSON across refresh. No FE-only memory of
// next state, no heuristic, no regex — the FE just forwards the wire
// resolution. See the BE service `resolve_next_step_for_submission`.

const NextStepTaskSchema = z.object({
    id: z.string(),
    module: z.string(),
    quarter: z.string().optional(),
    competency: z.object({ title: z.string(), body: z.string() }),
    brief: z.object({
        title: z.string(),
        body: z.string(),
        image: z.string().optional(),
    }),
    tags: z.array(z.string()),
    stats: z.object({
        avgScorePct: z.number().optional(),
        progress: z.object({ done: z.number(), total: z.number() }),
    }),
    hintCost: z.number().optional().nullable(),
    exampleResponse: z.string().optional(),
    // Forward-compat: TaskSerializer surfaces created_at/updated_at; the
    // consumer doesn't read them but tolerate so a future column
    // doesn't trip parse. Same `passthrough` posture as the active-task
    // brief — the FE-local ActiveTask type omits these on purpose.
    created_at: z.string().optional(),
    updated_at: z.string().optional(),
}).passthrough();

/**
 * Zod runtime guard for the next-step wire payload. Discriminated union
 * keyed by `kind` — drift surfaces as a typed `ResponseShapeError` (and
 * a Sentry event with the Zod issue list) instead of crashing the
 * MenteeMentorshipView render mid-callback.
 */
export const NextStepResolutionSchema = z.discriminatedUnion('kind', [
    z.object({
        kind: z.literal('next_task'),
        task: NextStepTaskSchema,
    }),
    z.object({
        kind: z.literal('cohort_complete'),
    }),
    z.object({
        kind: z.literal('awaiting_review'),
        submission_id: z.string(),
    }),
]);

/**
 * FE-shaped resolution (camelCase on the `awaiting_review.submission_id`
 * → `submissionId` rename so consumers don't have to remember which
 * branch uses snake_case). The `task` branch carries the
 * `ActiveTaskBrief` shape verbatim — same boundary the active-task
 * endpoint emits.
 */
export type NextStepResolution =
    | { kind: 'next_task'; task: ActiveTaskBrief }
    | { kind: 'cohort_complete' }
    | { kind: 'awaiting_review'; submissionId: string };

/**
 * Resolve the next step after a submission was finalized.
 *
 * The submissionId is the canonical Submission UUID (NOT the FE-side
 * `client_submission_id`); a non-existent UUID — or one that does not
 * belong to the requesting mentee — surfaces as a 404 the caller can
 * branch on. Cross-tenant existence is never leaked: the BE selector
 * returns None for both "no row" and "wrong tenant".
 */
export async function getNextStepForSubmission(
    submissionId: string,
    options: { signal?: AbortSignal } = {},
): Promise<NextStepResolution> {
    const response = await apiClient.get(
        url(`/api/mentorship/submissions/${submissionId}/next_task/`),
        options.signal ? { signal: options.signal } : {},
    );
    const wire = parseAtBoundary(NextStepResolutionSchema, response.data, {
        op: 'getNextStepForSubmission',
    });
    if (wire.kind === 'awaiting_review') {
        return { kind: 'awaiting_review', submissionId: wire.submission_id };
    }
    if (wire.kind === 'cohort_complete') {
        return { kind: 'cohort_complete' };
    }
    // next_task — the wire `task` is structurally a superset of
    // ActiveTaskBrief (TaskSerializer fields). The cast is the same
    // boundary one-way upgrade the active-task path performs.
    return {
        kind: 'next_task',
        task: wire.task as unknown as ActiveTaskBrief,
    };
}
